import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import api from '../../views/services/api';

const SEVERITIES = [
    { value: 'low', label: 'Baixa' },
    { value: 'medium', label: 'Média' },
    { value: 'high', label: 'Alta' },
    { value: 'critical', label: 'Crítica' }
];

const newRequestId = () =>
    window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
          });

// O status da integração é consultado uma vez por carregamento da página e compartilhado entre
// todos os botões (uma tabela com 50 lotes não faz 50 chamadas).
let statusPromise = null;
export const loadSgqStatus = () => {
    if (!statusPromise) {
        statusPromise = api
            .get('v1/sgq/status/')
            .then((r) => r.data)
            .catch(() => ({ enabled: false, can_open_nc: false }));
    }
    return statusPromise;
};

export const useSgqEnabled = () => {
    const [enabled, setEnabled] = useState(false);
    useEffect(() => {
        let cancelled = false;
        loadSgqStatus().then((s) => !cancelled && setEnabled(Boolean(s.enabled)));
        return () => {
            cancelled = true;
        };
    }, []);
    return enabled;
};

// Indicadores de NCs abertas de vários registros de uma vez: { [id]: { open, items[] } }.
export const fetchSgqIndicators = async (appId, entityType, ids) => {
    if (!ids.length) return {};
    const response = await api.get(`v1/apps/${appId}/sgq/indicators/`, {
        params: { entity_type: entityType, ids: ids.slice(0, 100).join(',') }
    });
    return response.data;
};

const openInSgq = async (ncId) => {
    const tab = window.open('', '_blank');
    try {
        const response = await api.get('v1/sgq/sso-link/', { params: { nc: ncId } });
        if (tab) tab.location.href = response.data.url;
        else window.location.assign(response.data.url);
    } catch (e) {
        if (tab) tab.close();
    }
};

// Indicador de NCs abertas + botão "Abrir NC" para um registro do ERP (lote, OP, recebimento,
// fornecedor). Não renderiza nada quando a conta não tem a integração com o SGQ ligada.
// `indicator` pode vir pronto (busca em lote); sem ele o componente consulta sozinho.
const SgqNc = ({ appId, entityType, entityId, defaultTitle = '', indicator, size = 'small', onOpened }) => {
    const enabled = useSgqEnabled();
    const [own, setOwn] = useState(null);
    const [open, setOpen] = useState(false);
    const [requestId, setRequestId] = useState('');
    const [form, setForm] = useState({ title: '', description: '', severity: 'medium' });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const refresh = useCallback(async () => {
        if (!appId || !entityId) return;
        try {
            const data = await fetchSgqIndicators(appId, entityType, [entityId]);
            setOwn(data[entityId] || { open: 0, items: [] });
        } catch (e) {
            setOwn({ open: 0, items: [] });
        }
    }, [appId, entityType, entityId]);

    useEffect(() => {
        if (enabled && indicator === undefined) refresh();
    }, [enabled, indicator, refresh]);

    if (!enabled || !entityId) return null;
    const data = indicator !== undefined ? indicator : own;
    const openItems = (data?.items || []).filter((i) => i.is_open);

    const openForm = () => {
        setRequestId(newRequestId()); // repetir o envio reaproveita a chave: não duplica a NC
        setForm({ title: defaultTitle, description: '', severity: 'medium' });
        setError('');
        setOpen(true);
    };

    const submit = async () => {
        setSubmitting(true);
        setError('');
        try {
            await api.post(`v1/apps/${appId}/sgq/open-nc/`, {
                entity_type: entityType,
                entity_id: entityId,
                request_id: requestId,
                title: form.title,
                description: form.description,
                severity: form.severity
            });
            setOpen(false);
            await refresh();
            if (onOpened) onOpened();
        } catch (e) {
            setError(e.response?.data?.error || 'Não foi possível abrir a não conformidade.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {openItems.map((nc) => (
                <Chip
                    key={nc.nc_id}
                    size="small"
                    color="warning"
                    label={`${nc.number} · ${nc.status_label || nc.status}`}
                    onClick={() => openInSgq(nc.nc_id)}
                    title="Abrir no SGQ"
                />
            ))}
            <Button size={size} color="warning" onClick={openForm}>
                Abrir NC
            </Button>

            <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Abrir não conformidade</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField
                        sx={{ mt: 1 }}
                        label="Título"
                        required
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                    />
                    <TextField
                        label="Descrição"
                        multiline
                        minRows={3}
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                    <TextField
                        select
                        label="Severidade"
                        value={form.severity}
                        onChange={(e) => setForm({ ...form, severity: e.target.value })}
                    >
                        {SEVERITIES.map((s) => (
                            <MenuItem key={s.value} value={s.value}>
                                {s.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                        O produto, o lote e o fornecedor deste registro seguem junto com a NC.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={submit} disabled={submitting || !form.title.trim()}>
                        {submitting ? <CircularProgress size={18} /> : 'Abrir NC'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SgqNc;
